import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import axiosInstance from '../api/axiosInstance';

function Teams() {
  const { isPlusActive, isLoading } = useSubscription();
  const locked = !isLoading && !isPlusActive;

  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamsError, setTeamsError] = useState(null);

  const [teamName, setTeamName] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);

  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState(null);

  // invites
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('MEMBER');
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  // properties linking
  const [allProperties, setAllProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [linkingProperty, setLinkingProperty] = useState(false);

  const isOwner = useMemo(() => selectedTeam?.myRole === 'OWNER', [selectedTeam?.myRole]);

  // Load teams list (only when unlocked)
  useEffect(() => {
    let mounted = true;

    async function loadTeams() {
      if (isLoading) return;

      if (locked) {
        if (mounted) {
          setTeams([]);
          setTeamsError(null);
          setTeamsLoading(false);
          setSelectedTeamId(null);
          setSelectedTeam(null);
        }
        return;
      }

      try {
        setTeamsLoading(true);
        setTeamsError(null);

        const res = await axiosInstance.get('/api/dashboard/teams', { withCredentials: true });
        const data = Array.isArray(res?.data) ? res.data : [];

        if (!mounted) return;
        setTeams(data);

        if (!selectedTeamId && data.length) setSelectedTeamId(data[0].id);
      } catch (err) {
        console.error('[Teams] loadTeams error:', err);
        if (!mounted) return;
        setTeamsError(err?.response?.data?.error || err.message || 'Failed to load teams');
        setTeams([]);
      } finally {
        if (mounted) setTeamsLoading(false);
      }
    }

    loadTeams();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, locked]);

  // Load selected team details (only when unlocked + selectedTeamId)
  useEffect(() => {
    let mounted = true;

    async function loadTeam() {
      if (isLoading) return;

      if (locked) {
        if (mounted) {
          setSelectedTeam(null);
          setTeamError(null);
          setTeamLoading(false);
        }
        return;
      }

      if (!selectedTeamId) {
        setSelectedTeam(null);
        return;
      }

      try {
        setTeamLoading(true);
        setTeamError(null);
        setInviteResult(null);

        const res = await axiosInstance.get(`/api/dashboard/teams/${selectedTeamId}`, {
          withCredentials: true,
        });

        if (!mounted) return;
        setSelectedTeam(res?.data ?? null);
      } catch (err) {
        console.error('[Teams] loadTeam error:', err);
        if (!mounted) return;
        setTeamError(err?.response?.data?.error || err.message || 'Failed to load team');
        setSelectedTeam(null);
      } finally {
        if (mounted) setTeamLoading(false);
      }
    }

    loadTeam();
    return () => {
      mounted = false;
    };
  }, [isLoading, locked, selectedTeamId]);

  // Load properties for linking (only when unlocked; safe to load once)
  useEffect(() => {
    let mounted = true;

    async function loadProps() {
      if (isLoading) return;

      if (locked) {
        if (mounted) {
          setAllProperties([]);
          setPropertiesLoading(false);
        }
        return;
      }

      try {
        setPropertiesLoading(true);
        const res = await axiosInstance.get('/api/dashboard/properties', { withCredentials: true });
        if (!mounted) return;
        setAllProperties(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        console.error('[Teams] loadProperties error:', err);
        if (!mounted) return;
        setAllProperties([]);
      } finally {
        if (mounted) setPropertiesLoading(false);
      }
    }

    loadProps();
    return () => {
      mounted = false;
    };
  }, [isLoading, locked]);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    const name = (teamName || '').trim();
    if (!name) return;

    try {
      setCreatingTeam(true);
      setTeamsError(null);

      const res = await axiosInstance.post(
        '/api/dashboard/teams',
        { name },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      );

      const created = res?.data;
      if (created?.id) {
        setTeams((prev) => [created, ...prev]);
        setSelectedTeamId(created.id);
      }
      setTeamName('');
    } catch (err) {
      console.error('[Teams] createTeam error:', err);
      setTeamsError(err?.response?.data?.error || err.message || 'Failed to create team');
    } finally {
      setCreatingTeam(false);
    }
  };

  // ✅ NEW: delete team
  const handleDeleteTeam = async () => {
    if (!selectedTeamId) return;
    if (!isOwner) return;

    const ok = window.confirm('Delete this team? This will remove access for all members and unlink all properties.');
    if (!ok) return;

    try {
      setTeamError(null);
      await axiosInstance.delete(`/api/dashboard/teams/${selectedTeamId}`, { withCredentials: true });

      // remove from list + select next
      setTeams((prev) => {
        const next = prev.filter((t) => t.id !== selectedTeamId);
        const nextSelected = next[0]?.id || null;
        setSelectedTeamId(nextSelected);
        return next;
      });

      setSelectedTeam(null);
    } catch (err) {
      console.error('[Teams] deleteTeam error:', err);
      setTeamError(err?.response?.data?.error || err.message || 'Failed to delete team');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!selectedTeamId || !memberId) return;

    try {
      await axiosInstance.delete(`/api/dashboard/teams/${selectedTeamId}/members/${memberId}`, {
        withCredentials: true,
      });

      const res = await axiosInstance.get(`/api/dashboard/teams/${selectedTeamId}`, { withCredentials: true });
      setSelectedTeam(res?.data ?? null);
    } catch (err) {
      console.error('[Teams] removeMember error:', err);
      setTeamError(err?.response?.data?.error || err.message || 'Failed to remove member');
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    if (!selectedTeamId) return;

    const email = (inviteEmail || '').trim();
    if (!email) return;

    try {
      setCreatingInvite(true);
      setTeamError(null);
      setInviteResult(null);

      const res = await axiosInstance.post(
        `/api/dashboard/teams/${selectedTeamId}/invites`,
        { email, role: inviteRole },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      );

      setInviteResult(res?.data ?? null);
      setInviteEmail('');

      const t = await axiosInstance.get(`/api/dashboard/teams/${selectedTeamId}`, { withCredentials: true });
      setSelectedTeam(t?.data ?? null);
    } catch (err) {
      console.error('[Teams] createInvite error:', err);
      setTeamError(err?.response?.data?.error || err.message || 'Failed to create invite');
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!selectedTeamId || !inviteId) return;

    try {
      await axiosInstance.delete(`/api/dashboard/teams/${selectedTeamId}/invites/${inviteId}`, {
        withCredentials: true,
      });

      const t = await axiosInstance.get(`/api/dashboard/teams/${selectedTeamId}`, { withCredentials: true });
      setSelectedTeam(t?.data ?? null);
    } catch (err) {
      console.error('[Teams] revokeInvite error:', err);
      setTeamError(err?.response?.data?.error || err.message || 'Failed to revoke invite' );
    }
  };

  const handleLinkProperty = async (e) => {
    e.preventDefault();
    if (!selectedTeamId) return;
    if (!selectedPropertyId) return;

    try {
      setLinkingProperty(true);
      setTeamError(null);

      await axiosInstance.post(
        `/api/dashboard/teams/${selectedTeamId}/properties`,
        { propertyId: selectedPropertyId },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      );

      setSelectedPropertyId('');

      const t = await axiosInstance.get(`/api/dashboard/teams/${selectedTeamId}`, { withCredentials: true });
      setSelectedTeam(t?.data ?? null);
    } catch (err) {
      console.error('[Teams] linkProperty error:', err);
      setTeamError(err?.response?.data?.error || err.message || 'Failed to link property');
    } finally {
      setLinkingProperty(false);
    }
  };

  const handleUnlinkProperty = async (propertyId) => {
    if (!selectedTeamId || !propertyId) return;

    try {
      await axiosInstance.delete(`/api/dashboard/teams/${selectedTeamId}/properties/${propertyId}`, {
        withCredentials: true,
      });

      const t = await axiosInstance.get(`/api/dashboard/teams/${selectedTeamId}`, { withCredentials: true });
      setSelectedTeam(t?.data ?? null);
    } catch (err) {
      console.error('[Teams] unlinkProperty error:', err);
      setTeamError(err?.response?.data?.error || err.message || 'Failed to unlink property');
    }
  };

  const copyToClipboard = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
    } catch (e) {
      console.error('copy failed', e);
    }
  };

  return (
    <div className="pt-10 md:pt-0">
      <div className={locked ? 'blur-md pointer-events-none' : ''}>
        <h1 className="text-4xl mb-6">Teams</h1>

        <div className="glass p-6 rounded-xl mb-6">
          <h2 className="text-xl text-white mb-4">Create a team</h2>
          <form onSubmit={handleCreateTeam} className="flex flex-col md:flex-row gap-3">
            <input
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Team name"
              className="flex-1 p-3 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
            />
            <button
              type="submit"
              disabled={creatingTeam}
              className="bg-[#AF9B8A] px-4 py-2 rounded-xl disabled:opacity-60"
            >
              {creatingTeam ? 'Creating…' : 'Create'}
            </button>
          </form>
          {teamsError && <div className="text-red-300 mt-3">{teamsError}</div>}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="glass p-6 rounded-xl">
            <h2 className="text-xl text-white mb-4">Your teams</h2>

            {teamsLoading ? (
              <div className="text-white/70">Loading teams…</div>
            ) : teams.length === 0 ? (
              <div className="text-white/70">No teams yet.</div>
            ) : (
              <div className="space-y-3">
                {teams.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTeamId(t.id)}
                    className={`w-full text-left p-4 rounded-xl border transition ${
                      selectedTeamId === t.id
                        ? 'bg-white/10 border-white/20'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <p className="text-white font-semibold">{t.name}</p>
                    <p className="text-white/70 text-sm">
                      Role: {t.myRole || '-'} • Members: {Array.isArray(t.members) ? t.members.length : 0} • Properties:{' '}
                      {Array.isArray(t.properties) ? t.properties.length : 0}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 glass p-6 rounded-xl">
            {!selectedTeamId ? (
              <div className="text-white/70">Select a team to view details.</div>
            ) : teamLoading ? (
              <div className="text-white/70">Loading team…</div>
            ) : teamError ? (
              <div className="text-red-300">{teamError}</div>
            ) : !selectedTeam ? (
              <div className="text-white/70">No team loaded.</div>
            ) : (
              <>
                <div className="mb-6 flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-2xl text-white font-semibold">{selectedTeam.name}</h2>
                    <p className="text-white/70 text-sm">Your role: {selectedTeam.myRole || '-'}</p>
                  </div>

                  {/* ✅ Delete team (OWNER only) */}
                  {isOwner && (
                    <button
                      onClick={handleDeleteTeam}
                      className="px-4 py-2 rounded-xl bg-white/10 border border-white/10 hover:bg-white/20 text-white"
                    >
                      Delete team
                    </button>
                  )}
                </div>

                {/* Members */}
                <div className="mb-8">
                  <h3 className="text-xl text-white mb-3">Members</h3>

                  <div className="space-y-2">
                    {(selectedTeam.members || []).map((m) => (
                      <div key={m.id} className="p-3 rounded-xl bg-white/5 border border-white/10 flex items-center gap-3">
                        <div className="flex-1">
                          <p className="text-white font-medium">
                            {m.user?.firstName || m.user?.name || m.user?.email || 'User'}
                          </p>
                          <p className="text-white/70 text-sm">{m.user?.email}</p>
                        </div>
                        <span className="text-white/80 text-sm px-3 py-1 rounded-full bg-white/10 border border-white/10">
                          {m.role}
                        </span>
                        {isOwner && (
                          <button
                            onClick={() => handleRemoveMember(m.id)}
                            className="px-3 py-1 rounded-lg bg-white/10 border border-white/10 hover:bg-white/20 text-white text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Invite */}
                  {isOwner && (
                    <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10">
                      <h4 className="text-white font-semibold mb-3">Invite a landlord</h4>
                      <form onSubmit={handleCreateInvite} className="flex flex-col md:flex-row gap-3">
                        <input
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="Email address"
                          className="flex-1 p-3 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
                        />
                        <select
                          value={inviteRole}
                          onChange={(e) => setInviteRole(e.target.value)}
                          className="p-3 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none"
                        >
                          <option value="MEMBER">MEMBER</option>
                          <option value="OWNER">OWNER</option>
                        </select>
                        <button
                          type="submit"
                          disabled={creatingInvite}
                          className="bg-[#AF9B8A] px-4 py-2 rounded-xl disabled:opacity-60"
                        >
                          {creatingInvite ? 'Creating…' : 'Create invite'}
                        </button>
                      </form>

                      {inviteResult?.inviteLink && (
                        <div className="mt-3">
                          <p className="text-white/80 text-sm mb-2">Invite link (copy and send):</p>
                          <div className="flex flex-col md:flex-row gap-2">
                            <input
                              readOnly
                              value={inviteResult.inviteLink}
                              className="flex-1 p-2 rounded-lg text-white bg-white/5 border border-white/10"
                            />
                            <button
                              onClick={() => copyToClipboard(inviteResult.inviteLink)}
                              className="px-4 py-2 rounded-xl bg-white/10 border border-white/10 hover:bg-white/20 text-white"
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                      )}

                      {(selectedTeam.invites || []).length > 0 && (
                        <div className="mt-4">
                          <p className="text-white/80 text-sm mb-2">Active invites</p>
                          <div className="space-y-2">
                            {selectedTeam.invites.map((inv) => (
                              <div
                                key={inv.id}
                                className="p-3 rounded-xl bg-white/5 border border-white/10 flex items-center gap-3"
                              >
                                <div className="flex-1">
                                  <p className="text-white">{inv.email}</p>
                                  <p className="text-white/70 text-sm">Role: {inv.role}</p>
                                </div>
                                <button
                                  onClick={() => handleRevokeInvite(inv.id)}
                                  className="px-3 py-1 rounded-lg bg-white/10 border border-white/10 hover:bg-white/20 text-white text-sm"
                                >
                                  Revoke
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Properties */}
                <div>
                  <h3 className="text-xl text-white mb-3">Linked properties</h3>

                  {(selectedTeam.properties || []).length === 0 ? (
                    <div className="text-white/70">No properties linked yet.</div>
                  ) : (
                    <div className="space-y-2 mb-4">
                      {selectedTeam.properties.map((tp) => (
                        <div
                          key={`${tp.teamId}-${tp.propertyId}`}
                          className="p-3 rounded-xl bg-white/5 border border-white/10 flex items-center gap-3"
                        >
                          <div className="flex-1">
                            <p className="text-white font-medium">{tp.property?.title || 'Property'}</p>
                            <p className="text-white/70 text-sm">
                              {tp.property?.address1 || ''} {tp.property?.town || ''} {tp.property?.postcode || ''}
                            </p>
                          </div>

                          {isOwner && (
                            <button
                              onClick={() => handleUnlinkProperty(tp.propertyId)}
                              className="px-3 py-1 rounded-lg bg-white/10 border border-white/10 hover:bg-white/20 text-white text-sm"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {isOwner && (
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <h4 className="text-white font-semibold mb-3">Add property to team</h4>

                      <form onSubmit={handleLinkProperty} className="flex flex-col md:flex-row gap-3">
                        <select
                          value={selectedPropertyId}
                          onChange={(e) => setSelectedPropertyId(e.target.value)}
                          className="flex-1 p-3 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none"
                          disabled={propertiesLoading}
                        >
                          <option value="">{propertiesLoading ? 'Loading properties…' : 'Select a property'}</option>
                          {allProperties.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.title} — {p.postcode}
                            </option>
                          ))}
                        </select>

                        <button
                          type="submit"
                          disabled={linkingProperty || !selectedPropertyId}
                          className="bg-[#AF9B8A] px-4 py-2 rounded-xl disabled:opacity-60"
                        >
                          {linkingProperty ? 'Adding…' : 'Add'}
                        </button>
                      </form>

                      <p className="text-white/60 text-xs mt-2">
                        Note: MVP only allows linking properties you own (safe default).
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Paywall overlay (identical UX to Analytics) */}
      {locked && (
        <div className="fixed inset-0 flex items-center justify-center">
          <div className="bg-black/70 p-8 rounded-2xl text-center">
            <p className="text-xl mb-4">Teams is a Beem Plus feature</p>
            <Link to="/settings" className="bg-[#AF9B8A] px-4 py-2 rounded-xl">
              Upgrade
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default Teams;
